/**
 * Data layer for `localized-region-specific-dns-cdn-edge`. Thin typed fetch wrappers over /api/v1/localized-region-specific-dns-cdn-edge.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export interface LocalizedRegionSpecificDnsCdnEdgeRow {
	id: string;
	tenant_id: string;
	created_at: number;
	updated_at: number;
	[key: string]: unknown;
}

export interface ListLocalizedRegionSpecificDnsCdnEdgeResponse {
	count: number;
	items: LocalizedRegionSpecificDnsCdnEdgeRow[];
}

export interface CreateLocalizedRegionSpecificDnsCdnEdgeBody {
	[key: string]: unknown;
}

const BASE = "/api/v1/localized-region-specific-dns-cdn-edge";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListLocalizedRegionSpecificDnsCdnEdgeResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListLocalizedRegionSpecificDnsCdnEdgeResponse>(res);
}

export async function create(body: CreateLocalizedRegionSpecificDnsCdnEdgeBody): Promise<LocalizedRegionSpecificDnsCdnEdgeRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<LocalizedRegionSpecificDnsCdnEdgeRow>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

export const localizedRegionSpecificDnsCdnEdgeQueryKeys = {
	all: ["localized-region-specific-dns-cdn-edge"] as const,
	list: () => [...localizedRegionSpecificDnsCdnEdgeQueryKeys.all] as const,
	detail: (id: string) => [...localizedRegionSpecificDnsCdnEdgeQueryKeys.all, id] as const,
};
